import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, Link } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { Html5Qrcode } from 'html5-qrcode';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import {
  Flag, QrCode, ScanLine, Trophy, Clock, Battery, Target,
  Lock, Zap, Bot, Car,
} from 'lucide-react';
import { api, compact, timeAgo } from '../lib/api';
import { useAuth } from '../lib/store';
import { getSocket } from '../lib/socket';
import { themeOf, ARENA } from '../lib/classTheme';
import {
  ArenaFloor, ArenaTitle, ArenaTabs, GoldFrame, Pedestal,
  Laurel, ArenaEmpty, ArenaStatBar,
} from '../components/ArenaFrame';
import { Avatar, Sheet, SkeletonList, RankBadge } from '../components/ui';

const DIFFICULTY = {
  easy:    { color: '#27ae60', label: 'Easy',    stars: 1 },
  medium:  { color: '#c9973a', label: 'Medium',  stars: 2 },
  hard:    { color: '#d35400', label: 'Hard',    stars: 3 },
  extreme: { color: '#8b2635', label: 'Extreme', stars: 4 },
};

export default function RaceHub() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const [tab, setTab] = useState('tracks');
  const [track, setTrack] = useState(null);
  const [stake, setStake] = useState(0);
  const [qrOpen, setQrOpen] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [invite, setInvite] = useState(null);

  const { data: car } = useQuery({ queryKey: ['car'], queryFn: () => api.get('/cars').then(r => r.data) });
  const { data: tracks } = useQuery({ queryKey: ['tracks'], queryFn: () => api.get('/races/tracks').then(r => r.data) });
  const { data: active } = useQuery({
    queryKey: ['activeRaces'], queryFn: () => api.get('/races/active/me').then(r => r.data),
    refetchInterval: 15000,
  });
  const { data: lb } = useQuery({
    queryKey: ['raceLb'], queryFn: () => api.get('/races/leaderboard').then(r => r.data),
    enabled: tab === 'ranking',
  });
  const { data: history } = useQuery({
    queryKey: ['raceHistory'], queryFn: () => api.get('/races/history/me').then(r => r.data),
    enabled: tab === 'records',
  });

  const inviteM = useMutation({
    mutationFn: () => api.post('/races/invite', { trackId: track?.id, stake }),
    onSuccess: ({ data }) => { setInvite(data); setQrOpen(true); },
    onError: e => toast.error(e.message),
  });

  const npcM = useMutation({
    mutationFn: () => api.post('/races/npc', { trackId: track?.id }),
    onSuccess: ({ data }) => { toast.success('Race starting! 🏁'); nav(`/race/${data.raceId}`); },
    onError: e => toast.error(e.message),
  });

  useEffect(() => {
    const s = getSocket(); if (!s) return;
    const onStart = ({ raceId }) => { toast.success('Engines roaring! 🏁'); nav(`/race/${raceId}`); };
    s.on('race:started', onStart);
    return () => s.off('race:started', onStart);
  }, [nav]);

  useEffect(() => { if (tracks?.length && !track) setTrack(tracks[0]); }, [tracks, track]);

  const myCar = car?.car;
  const t = themeOf(car?.classKey);

  if (!myCar) {
    return (
      <ArenaEmpty emoji="🏎️" title="No chariot yet"
        sub="Claim your first chariot in the garage to enter the Circus Maximus."
        action={<Link to="/garage" className="btn-primary btn-sm">Go to garage</Link>} />
    );
  }

  return (
    <div className="space-y-4 animate-fade-up">
      {/* ═══ HEADER ═══ */}
      <div className="card-arena arena-arches overflow-hidden">
        <ArenaFloor classKey={car.classKey} className="p-5" showCrowd>
          <div className="flex items-center gap-4">
            <Pedestal glow={t.hex} width="80%">
              <div className="w-28 h-24 rounded-xl overflow-hidden"
                style={{ padding: 2, background: ARENA.goldFrame, boxShadow: t.glowCss }}>
                <div className="w-full h-full rounded-[10px] overflow-hidden bg-stone-900">
                  <img src={myCar.image} alt="" className="w-full h-full object-cover" />
                </div>
              </div>
            </Pedestal>

            <div className="flex-1 min-w-0 text-white">
              <div className="flex items-center gap-1.5 mb-1">
                <Laurel size={22} color={ARENA.goldL} />
                <span className="text-[10px] font-display font-black uppercase tracking-[0.2em]"
                  style={{ color: ARENA.goldL }}>Circus Maximus</span>
              </div>
              <h1 className="font-display text-2xl font-extrabold tracking-wide"
                style={{ color: ARENA.goldL, textShadow: '0 2px 16px rgba(201,151,58,.5)' }}>
                Racing
              </h1>
              <p className="text-xs font-semibold" style={{ color: 'rgba(232,220,200,.7)' }}>
                {myCar.name} · Lv{myCar.level}
              </p>

              <div className="grid grid-cols-3 gap-1.5 mt-3">
                <Mini l="ELO"  v={myCar.raceElo} />
                <Mini l="WINS" v={`${myCar.wins}/${myCar.races}`} />
                <Mini l="TUNE" v={myCar.tuningScore} />
              </div>
            </div>
          </div>
        </ArenaFloor>
      </div>

      <ArenaTabs value={tab} onChange={setTab} tabs={[
        { key: 'tracks',  label: 'Circuits', icon: '🏁' },
        { key: 'ranking', label: 'Ranking', icon: '🏆' },
        { key: 'records', label: 'Records', icon: '📜' },
      ]} />

      {/* ═══ TRACKS ═══ */}
      {tab === 'tracks' && (
        <>
          {active?.races?.length > 0 && (
            <div>
              <ArenaTitle title="Ongoing races" latin="Cursus" laurel />
              <div className="space-y-2">
                {active.races.map(r => (
                  <Link key={r.id} to={`/race/${r.id}`} className="card-h p-3 flex items-center gap-3"
                    style={{ borderColor: ARENA.gold, borderWidth: 2 }}>
                    <div className="w-10 h-10 rounded-xl grid place-items-center text-white pulse-ring"
                      style={{ background: ARENA.goldFrame }}>
                      <Flag size={18} />
                    </div>
                    <div className="flex-1">
                      <p className="font-display font-bold text-sm">{r.track_name}</p>
                      <p className="text-[11px] font-semibold" style={{ color: 'var(--stone-400)' }}>
                        {r.p1} vs {r.p2} · segment {r.segment_index + 1}/{r.total_segments}
                      </p>
                    </div>
                    <span className="chip-gold">CONTINUE</span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {active?.invites?.length > 0 && (
            <div>
              <ArenaTitle title="Challenges" latin="Provocationes" />
              {active.invites.map(i => (
                <Link key={i.id} to={`/race/accept/${i.code}`} className="card-h p-3 flex items-center gap-3">
                  <span className="text-2xl">🏁</span>
                  <div className="flex-1">
                    <p className="font-display font-bold text-sm">@{i.username} challenges you</p>
                    <p className="text-[11px] font-semibold" style={{ color: 'var(--stone-400)' }}>
                      {i.track_name} · {i.stake ? `💎${i.stake}` : 'Honor race'}
                    </p>
                  </div>
                  <span className="btn-primary btn-sm">Accept</span>
                </Link>
              ))}
            </div>
          )}

          <ArenaTitle title="Choose circuit" latin="Elige Circum" />
          <div className="space-y-2">
            {tracks?.map(tr => {
              const d = DIFFICULTY[tr.difficulty];
              const sel = track?.id === tr.id;
              return (
                <button key={tr.id} onClick={() => !tr.locked && setTrack(tr)}
                  disabled={tr.locked}
                  className={`w-full card-h p-3 flex items-center gap-3 text-left transition
                    ${tr.locked ? 'opacity-50' : ''}`}
                  style={sel ? {
                    borderColor: ARENA.gold, borderWidth: 2,
                    boxShadow: '0 0 0 1px rgba(201,151,58,.25), 0 4px 20px -6px rgba(201,151,58,.4)',
                  } : {}}>
                  <div className="w-11 h-11 rounded-xl grid place-items-center shrink-0 text-white"
                    style={{ background: sel ? ARENA.goldFrame : `${d.color}22`, color: sel ? '#fff' : d.color }}>
                    {tr.locked ? <Lock size={17} /> : <Flag size={17} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <p className="font-display font-bold text-sm truncate">{tr.name}</p>
                      <span className="chip" style={{ background: `${d.color}22`, color: d.color }}>
                        {'★'.repeat(d.stars)}
                      </span>
                    </div>
                    <p className="text-[11px] mt-0.5 line-clamp-1" style={{ color: 'var(--stone-500)' }}>
                      {tr.description}
                    </p>
                    <p className="text-[10px] font-semibold mt-0.5" style={{ color: 'var(--stone-400)' }}>
                      {tr.laps} laps · {tr.totalSegments} segments
                      {tr.locked && ` · needs Lv${tr.min_level}`}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>

          {/* actions */}
          <div className="card p-3 space-y-2">
            <div className="flex items-center gap-2">
              <label className="label !mb-0 shrink-0">Wager</label>
              <div className="flex gap-1.5 flex-1 overflow-x-auto no-scrollbar">
                {[0, 50, 100, 250, 500].map(v => (
                  <button key={v} onClick={() => setStake(v)}
                    className="px-3 py-1.5 rounded-lg text-xs font-black whitespace-nowrap transition"
                    style={stake === v
                      ? { background: ARENA.goldFrame, color: '#fff' }
                      : { background: 'var(--stone-100)', color: 'var(--stone-500)' }}>
                    {v === 0 ? 'Honor' : `💎${v}`}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2">
              <button onClick={() => inviteM.mutate()} disabled={!track || inviteM.isPending}
                className="btn-primary btn-sm flex-col !py-2.5 h-auto">
                <QrCode size={15} /><span className="text-[10px] font-display">Show QR</span>
              </button>
              <button onClick={() => setScanOpen(true)} className="btn-dark btn-sm flex-col !py-2.5 h-auto">
                <ScanLine size={15} /><span className="text-[10px] font-display">Scan</span>
              </button>
              <button onClick={() => npcM.mutate()} disabled={!track || npcM.isPending}
                className="btn-ghost btn-sm flex-col !py-2.5 h-auto">
                <Bot size={15} /><span className="text-[10px] font-display">vs AI</span>
              </button>
            </div>
          </div>
        </>
      )}

      {/* ═══ RANKING ═══ */}
      {tab === 'ranking' && (
        !lb ? <SkeletonList n={8} h="h-16" />
        : <div className="card p-2 space-y-1">
            {lb.map(r => (
              <Link key={r.username} to={`/u/${r.username}`}
                className="flex items-center gap-3 p-2.5 rounded-xl transition hover:bg-stone-100"
                style={r.username === user.username ? { background: 'rgba(201,151,58,.10)' } : {}}>
                <RankBadge rank={r.rank} />
                <div className="w-11 h-9 rounded-lg overflow-hidden shrink-0"
                  style={{ padding: 1.5, background: ARENA.goldFrame }}>
                  <div className="w-full h-full rounded overflow-hidden bg-stone-200">
                    <img src={r.image_url} alt="" className="w-full h-full object-cover" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <p className="font-display font-bold text-sm truncate">{r.car_name}</p>
                    {r.is_npc ? <Bot size={11} style={{ color: 'var(--stone-400)' }} /> : null}
                  </div>
                  <p className="text-[11px] font-semibold" style={{ color: 'var(--stone-400)' }}>
                    @{r.username} · {r.wins}W/{r.races}R
                    {r.city_handle && ` · ${r.city_handle}`}
                  </p>
                </div>
                <span className="font-display font-black text-sm" style={{ color: ARENA.goldD }}>
                  {r.race_elo}
                </span>
              </Link>
            ))}
          </div>
      )}

      {/* ═══ RECORDS ═══ */}
      {tab === 'records' && (
        !history?.length
          ? <ArenaEmpty emoji="📜" title="No races yet" sub="Enter the Circus Maximus to begin." />
          : <div className="card p-2 space-y-1">
              {history.slice(0, 20).map(r => {
                const isP1 = r.p1_id === user.id;
                const opp = isP1 ? r.p2_name : r.p1_name;
                const oppImg = isP1 ? r.p2_img : r.p1_img;
                const won = r.winner_id === user.id;
                const myTime = isP1 ? r.p1_time_ms : r.p2_time_ms;
                return (
                  <Link key={r.id} to={`/race/${r.id}`}
                    className="flex items-center gap-3 p-2.5 rounded-xl transition hover:bg-stone-100">
                    <div className="w-10 h-8 rounded-lg overflow-hidden shrink-0"
                      style={{ padding: 1.5, background: ARENA.goldFrame }}>
                      <div className="w-full h-full rounded overflow-hidden bg-stone-200">
                        {oppImg && <img src={oppImg} alt="" className="w-full h-full object-cover" />}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-display font-bold text-sm truncate">{r.track_name}</p>
                      <p className="text-[11px] font-semibold" style={{ color: 'var(--stone-400)' }}>
                        vs @{opp} · {(myTime / 1000).toFixed(1)}s · {timeAgo(r.created_at)}
                      </p>
                    </div>
                    <span className={r.state === 'active' ? 'chip-ink' : won ? 'chip-gold' : 'chip-blood'}>
                      {r.state === 'active' ? 'LIVE' : won ? 'WIN' : 'LOSS'}
                    </span>
                  </Link>
                );
              })}
            </div>
      )}

      {/* ═══ QR ═══ */}
      <Sheet open={qrOpen} onClose={() => setQrOpen(false)} title="Your race sigil">
        <div className="text-center">
          <GoldFrame className="inline-block p-4" thick>
            {invite && <QRCodeSVG value={invite.qrPayload} size={190} level="M" fgColor="#241e16" />}
          </GoldFrame>
          <p className="mt-4 font-display font-black text-2xl tracking-[0.25em] grad-text">
            {invite?.code}
          </p>
          <p className="text-sm mt-2" style={{ color: 'var(--stone-500)' }}>
            {invite?.track?.name}<br />
            {invite?.stake > 0 && <b>Wager: 💎{invite.stake}</b>}
          </p>
          <div className="chip-ink mt-3"><Clock size={11} /> Expires in 15 minutes</div>
        </div>
      </Sheet>

      <Sheet open={scanOpen} onClose={() => setScanOpen(false)} title="Scan race sigil">
        <RaceScanner onCode={(c) => { setScanOpen(false); nav(`/race/accept/${c}`); }} />
      </Sheet>
    </div>
  );
}

function Mini({ l, v }) {
  return (
    <div className="rounded-lg px-2 py-1.5 text-center"
      style={{ background: 'rgba(232,200,116,.14)', border: '1px solid rgba(232,200,116,.22)' }}>
      <p className="text-[8px] font-display font-bold tracking-wider"
        style={{ color: 'rgba(232,220,200,.6)' }}>{l}</p>
      <p className="font-display font-black text-sm" style={{ color: ARENA.goldL }}>{v}</p>
    </div>
  );
}

function RaceScanner({ onCode }) {
  const [manual, setManual] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    let scanner;
    (async () => {
      try {
        scanner = new Html5Qrcode('race-qr');
        ref.current = scanner;
        await scanner.start({ facingMode: 'environment' },
          { fps: 10, qrbox: { width: 220, height: 220 } },
          (text) => {
            const code = text.startsWith('wof:race:') ? text.slice(9) : text.trim();
            scanner.stop().catch(() => {});
            onCode(code);
          }, () => {});
      } catch { toast.error('Camera unavailable'); }
    })();
    return () => { try { ref.current?.stop(); } catch {} };
  }, [onCode]);

  return (
    <div>
      <GoldFrame dark><div id="race-qr" className="aspect-square" /></GoldFrame>
      <div className="divider-laurel"><span>🏁</span></div>
      <label className="label">Or enter the code</label>
      <div className="flex gap-2">
        <input className="input font-display font-black tracking-widest text-center uppercase"
          value={manual} maxLength={16} onChange={e => setManual(e.target.value.toUpperCase())} />
        <button className="btn-primary" disabled={manual.length < 6}
          onClick={() => onCode(manual)}>Enter</button>
      </div>
    </div>
  );
}